import React, { useState, useEffect, useRef } from 'react';
import {
  X, Save, Plus, Trash2, RotateCcw,
  Package, Scan, CheckCircle, AlertCircle
} from 'lucide-react';
import apiClient from '../services/api';
import { useNavigate } from 'react-router-dom';

const VIOLET    = '#667eea';
const VIOLET_DK = '#764ba2';
const TEAL      = '#10b981';
const TEAL_DK   = '#059669';

const Outscan = ({ onClose }) => {
  const navigate     = useNavigate();
  const scanInputRef = useRef(null);

  const [loading,      setLoading]      = useState(false);
  const [error,        setError]        = useState('');
  const [success,      setSuccess]      = useState('');
  const [outscanForm,  setOutscanForm]  = useState({
    OutscanNo:'', OutscanDate:new Date().toISOString().slice(0,16),
    VehicleID:'', DriverID:'', DestinationCityID:'', RouteID:'',
    EmployeeID:'', Notes:'', Status:'Pending',
  });
  const [scannedItems,  setScannedItems]  = useState([]);
  const [scanInput,     setScanInput]     = useState('');
  const [scanningMode,  setScanningMode]  = useState(true);
  const [vehicles,      setVehicles]      = useState([]);
  const [drivers,       setDrivers]       = useState([]);
  const [cities,        setCities]        = useState([]);
  const [employees,     setEmployees]     = useState([]);
  const [routes,        setRoutes]        = useState([]);
  const [stats,         setStats]         = useState({ totalItems:0, totalWeight:0, totalPackages:0 });

  function getSessionData() {
    try { const s=localStorage.getItem('ltms_session'); if(!s) return null; const d=JSON.parse(s); return d?.session||d; } catch { return null; }
  }
  const session = getSessionData();

  useEffect(() => { loadDropdownData(); generateOutscanNumber(); }, []);
  useEffect(() => { if(scanningMode&&scanInputRef.current) scanInputRef.current.focus(); },[scanningMode,scannedItems]);
  useEffect(() => {
    setStats({
      totalItems:    scannedItems.length,
      totalWeight:   scannedItems.reduce((s,i)=>s+parseFloat(i.Weight||0),0),
      totalPackages: scannedItems.reduce((s,i)=>s+parseInt(i.NoOfPkgs||0),0),
    });
  },[scannedItems]);

  const loadDropdownData = async () => {
    try {
      const [vehRes,driverRes,cityRes,empRes,routeRes] = await Promise.all([
        apiClient.get('/Vehicles.php' ).catch(()=>({data:{success:false}})),
        apiClient.get('/Drivers.php'  ).catch(()=>({data:{success:false}})),
        apiClient.get('/Cities.php'   ).catch(()=>({data:{success:false}})),
        apiClient.get('/Employees.php').catch(()=>({data:{success:false}})),
        apiClient.get('/Routes.php'   ).catch(()=>({data:{success:false}})),
      ]);
      if(vehRes.data.success)    setVehicles(vehRes.data.data||[]);
      if(driverRes.data.success) setDrivers((driverRes.data.data||[]).filter(d=>parseInt(d.isactive)===1));
      if(cityRes.data.success)   setCities(cityRes.data.data||[]);
      if(empRes.data.success)    setEmployees(empRes.data.data||[]);
      if(routeRes.data.success)  setRoutes(routeRes.data.data||[]);
    } catch(err){ console.error('Error loading dropdown data:',err); }
  };

  const generateOutscanNumber = async () => {
    try {
      const res = await apiClient.get('/Outscan.php/next-number');
      if(res.data.success) setOutscanForm(prev=>({...prev,OutscanNo:res.data.data.outscanNo}));
    } catch { setOutscanForm(prev=>({...prev,OutscanNo:`OUT-${Date.now()}`})); }
  };

  const handleFormChange = (e) => {
    const{name,value}=e.target; setOutscanForm(prev=>({...prev,[name]:value}));
  };

  const handleScanSubmit = async (e) => {
    e.preventDefault();
    if(!scanInput.trim()) return;
    if(scannedItems.find(item=>item.ConsignmentNo===scanInput.trim())) {
      setError(`Consignment ${scanInput.trim()} already scanned!`);
      setScanInput(''); setTimeout(()=>setError(''),3000); return;
    }
    setLoading(true); setError('');
    try {
      const res = await apiClient.get(`/Outscan.php/consignment/${scanInput.trim()}`);
      if(res.data.success&&res.data.data) {
        const c=res.data.data;
        if(c.IsOutscanned===1||c.OutscanID) {
          setError(`Consignment ${scanInput.trim()} already outscanned on ${new Date(c.OutscanDate).toLocaleString()}`);
          setScanInput(''); setTimeout(()=>setError(''),5000); return;
        }
        setScannedItems(prev=>[...prev,{
          ConsignmentID:c.ConsignmentID, ConsignmentNo:c.ConsignmentNo,
          ConsignmentDate:c.ConsignmentDate, ShipperName:c.ShipperName,
          ConsigneeName:c.ConsigneeName, DestinationCity:c.DestinationCity,
          Weight:c.Weight||0, NoOfPkgs:c.NoOfPkgs||0,
          Status:c.Status, ScannedAt:new Date().toISOString(),
        }]);
        setSuccess(`✓ Scanned: ${scanInput.trim()}`);
        setScanInput(''); setTimeout(()=>setSuccess(''),2000); playBeep();
      } else {
        setError(`Consignment ${scanInput.trim()} not found!`);
        setScanInput(''); setTimeout(()=>setError(''),3000);
      }
    } catch(err) {
      setError('Error scanning: '+(err.response?.data?.message||err.message));
      setTimeout(()=>setError(''),3000);
    } finally { setLoading(false); }
  };

  const playBeep = () => {
    try {
      const ctx=new(window.AudioContext||window.webkitAudioContext)();
      const osc=ctx.createOscillator(),gain=ctx.createGain();
      osc.connect(gain); gain.connect(ctx.destination);
      osc.frequency.value=800; osc.type='sine';
      gain.gain.setValueAtTime(0.3,ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01,ctx.currentTime+0.1);
      osc.start(ctx.currentTime); osc.stop(ctx.currentTime+0.1);
    } catch { /* silent */ }
  };

  const handleRemoveItem = (index) => setScannedItems(prev=>prev.filter((_,i)=>i!==index));

  const handleSaveOutscan = async () => {
    if(scannedItems.length===0){ setError('Please scan at least one consignment'); return; }
    if(!outscanForm.VehicleID) { setError('Please select a vehicle');              return; }
    if(!outscanForm.DriverID)  { setError('Please select a driver');               return; }
    setLoading(true); setError(''); setSuccess('');
    try {
      const res = await apiClient.post('/Outscan.php',{
        ...outscanForm,
        Items:scannedItems.map(i=>({ConsignmentID:i.ConsignmentID,ConsignmentNo:i.ConsignmentNo})),
        TotalItems:stats.totalItems, TotalWeight:stats.totalWeight, TotalPackages:stats.totalPackages,
      });
      if(res.data.success) {
        setSuccess(`Outscan ${outscanForm.OutscanNo} saved successfully! ${stats.totalItems} items outscanned.`);
        setTimeout(()=>handleClearAll(),2000);
      } else setError(res.data.message||'Failed to save outscan');
    } catch(err) { setError('Error saving outscan: '+(err.response?.data?.message||err.message)); }
    finally { setLoading(false); }
  };

  const handleClearAll = () => {
    setOutscanForm({ OutscanNo:'', OutscanDate:new Date().toISOString().slice(0,16), VehicleID:'', DriverID:'', DestinationCityID:'', RouteID:'', EmployeeID:'', Notes:'', Status:'Pending' });
    setScannedItems([]); setScanInput(''); setError(''); setSuccess('');
    generateOutscanNumber();
  };

  const handleClose = () => { if(onClose) onClose(); else navigate('/dashboard'); };

  // shared field classes
  const inpCls = 'w-full px-3 py-2 border border-gray-200 rounded-md text-[13.5px] font-[inherit] text-gray-700 bg-white outline-none transition-all duration-150 focus:border-violet-400 focus:shadow-[0_0_0_3px_rgba(102,126,234,0.1)] box-border';
  const selCls = inpCls + ' cursor-pointer';
  const lblCls = 'block text-[11.5px] font-semibold text-gray-600 uppercase tracking-wide mb-1.5';

  if (!session?.companyid) {
    return (
      <div className="fixed inset-0 bg-black/55 flex items-center justify-center z-[1000] p-4 backdrop-blur-sm"
        onClick={e=>{if(e.target===e.currentTarget)handleClose();}}>
        <div className="bg-[#f5f5f8] rounded-[14px] w-full flex flex-col overflow-hidden shadow-2xl" style={{ maxWidth:520 }}
          onClick={e=>e.stopPropagation()}>
          <div className="flex items-center justify-between px-7 py-5 flex-shrink-0 border-b-[3px] border-white/15"
            style={{ background:`linear-gradient(135deg,${VIOLET},${VIOLET_DK})` }}>
            <div className="flex items-center gap-4">
              <div className="w-[50px] h-[50px] bg-white/22 rounded-xl flex items-center justify-center text-white flex-shrink-0"><Scan size={22}/></div>
              <h2 className="text-white text-[1.45rem] font-bold m-0" style={{ textShadow:'0 2px 4px rgba(0,0,0,0.2)' }}>Outscan</h2>
            </div>
            <button onClick={handleClose} className="w-10 h-10 bg-white/18 border-[1.5px] border-white/30 rounded-xl flex items-center justify-center text-white cursor-pointer hover:bg-white/30 transition-all duration-150"><X size={18}/></button>
          </div>
          <div className="p-10 text-center text-gray-500 text-sm">Please select a company and financial year from the dashboard.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/55 flex items-center justify-center z-[1000] p-4 backdrop-blur-sm"
      onClick={e=>{if(e.target===e.currentTarget)handleClose();}}>
      <div className="bg-[#f5f5f8] rounded-[14px] w-full flex flex-col overflow-hidden shadow-2xl" style={{ maxWidth:1100,maxHeight:'95vh' }}
        onClick={e=>e.stopPropagation()}>
        <style>{`
          @keyframes os-spin  { to{transform:rotate(360deg)} }
          @keyframes os-pulse { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:.5;transform:scale(1.3)} }
          .os-tr:hover td { background:#faf8ff !important; }
          .os-tr:nth-child(even) td { background:#f9fafb; }
        `}</style>

        {/* Header */}
        <div className="flex items-center justify-between px-7 py-5 flex-shrink-0 border-b-[3px] border-white/15"
          style={{ background:`linear-gradient(135deg,${VIOLET},${VIOLET_DK})`, boxShadow:'0 4px 16px rgba(102,126,234,0.35)' }}>
          <div className="flex items-center gap-4">
            <div className="w-[50px] h-[50px] bg-white/22 rounded-xl flex items-center justify-center text-white flex-shrink-0 shadow-lg"><Scan size={24}/></div>
            <div>
              <h2 className="text-white text-[1.45rem] font-bold m-0" style={{ textShadow:'0 2px 4px rgba(0,0,0,0.2)' }}>Outscan – Dispatch Scanning</h2>
              <p className="text-white/75 text-[0.8rem] m-0 font-normal">Scan consignments to dispatch</p>
            </div>
          </div>
          <button onClick={handleClose} className="w-10 h-10 bg-white/18 border-[1.5px] border-white/30 rounded-xl flex items-center justify-center text-white cursor-pointer hover:bg-white/30 hover:scale-105 transition-all duration-150 flex-shrink-0">
            <X size={18}/>
          </button>
        </div>

        {/* Toolbar */}
        <div className="flex gap-2.5 px-6 py-3 bg-white border-b border-gray-200 flex-wrap flex-shrink-0 items-center">
          <button onClick={handleSaveOutscan} disabled={loading||scannedItems.length===0}
            className="flex items-center gap-2 px-4 py-2 border-0 rounded-lg text-[13.5px] font-semibold text-white cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-150 hover:-translate-y-px whitespace-nowrap"
            style={{ background:`linear-gradient(135deg,${VIOLET},${VIOLET_DK})` }}>
            <Save size={16}/> Save Outscan ({stats.totalItems} items)
          </button>
          <button onClick={handleClearAll} disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg text-[13.5px] font-medium text-gray-700 cursor-pointer hover:border-violet-400 hover:text-violet-600 hover:bg-violet-50 disabled:opacity-50 transition-all duration-150">
            <RotateCcw size={16}/> Clear All
          </button>
          <button onClick={()=>setScanningMode(prev=>!prev)}
            className="flex items-center gap-2 px-4 py-2 border-0 rounded-lg text-[13.5px] font-semibold text-white cursor-pointer transition-all duration-150 whitespace-nowrap"
            style={{ background:scanningMode?TEAL:'#6b7280' }}>
            <Scan size={16}/> {scanningMode?'Scanning Active':'Scanning Paused'}
          </button>
        </div>

        {/* Alerts */}
        {error  &&<div className="flex items-center gap-2.5 px-6 py-3 bg-red-50 border-b border-red-200 text-red-600 text-[13.5px] flex-shrink-0"><AlertCircle size={18}/><strong>Error:</strong> {error}</div>}
        {success&&<div className="flex items-center gap-2.5 px-6 py-3 bg-emerald-50 border-b border-emerald-200 text-emerald-700 text-[13.5px] flex-shrink-0"><CheckCircle size={18}/><strong>Success:</strong> {success}</div>}

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 bg-[#f5f5f8] [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-thumb]:bg-violet-300 [&::-webkit-scrollbar-thumb]:rounded">

          {/* Outscan Details */}
          <div className="bg-white rounded-lg p-5 mb-4 border border-gray-200 shadow-sm">
            <div className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-3.5 pb-2.5 border-b-2 border-violet-100">
              <Package size={16} style={{ color:VIOLET }}/> Outscan Details
            </div>
            <div className="grid gap-3.5" style={{ gridTemplateColumns:'repeat(4,1fr)' }}>
              <div><label className={lblCls}>Outscan No *</label>
                <input type="text" name="OutscanNo" value={outscanForm.OutscanNo} readOnly className={`${inpCls} bg-blue-50 font-bold text-blue-700 cursor-not-allowed`}/></div>
              <div><label className={lblCls}>Outscan Date &amp; Time *</label>
                <input type="datetime-local" name="OutscanDate" value={outscanForm.OutscanDate} onChange={handleFormChange} required className={inpCls}/></div>
              <div><label className={lblCls}>Vehicle *</label>
                <select name="VehicleID" value={outscanForm.VehicleID} onChange={handleFormChange} required className={selCls}>
                  <option value="">Select Vehicle</option>
                  {vehicles.map(v=><option key={v.VehicleID} value={v.VehicleID}>{v.RegistrationNo}{v.VehicleType?` – ${v.VehicleType}`:''}</option>)}
                </select></div>
              <div><label className={lblCls}>Driver *</label>
                <select name="DriverID" value={outscanForm.DriverID} onChange={handleFormChange} required className={selCls}>
                  <option value="">Select Driver</option>
                  {drivers.map(d=><option key={d.driverid} value={d.driverid}>{d.drivername}</option>)}
                </select></div>
              <div><label className={lblCls}>Destination City</label>
                <select name="DestinationCityID" value={outscanForm.DestinationCityID} onChange={handleFormChange} className={selCls}>
                  <option value="">Select Destination</option>
                  {cities.map(c=><option key={c.cityid} value={c.cityid}>{c.cityname}</option>)}
                </select></div>
              <div><label className={lblCls}>Route</label>
                <select name="RouteID" value={outscanForm.RouteID} onChange={handleFormChange} className={selCls}>
                  <option value="">Select Route</option>
                  {routes.map(r=><option key={r.routeid} value={r.routeid}>{r.routename}</option>)}
                </select></div>
              <div><label className={lblCls}>Scanned By (Employee)</label>
                <select name="EmployeeID" value={outscanForm.EmployeeID} onChange={handleFormChange} className={selCls}>
                  <option value="">Select Employee</option>
                  {employees.map(e=><option key={e.employeeid} value={e.employeeid}>{e.employeename}</option>)}
                </select></div>
              <div><label className={lblCls}>Status</label>
                <select name="Status" value={outscanForm.Status} onChange={handleFormChange} className={selCls}>
                  <option value="Pending">Pending</option>
                  <option value="Dispatched">Dispatched</option>
                  <option value="In Transit">In Transit</option>
                </select></div>
              <div className="col-span-4"><label className={lblCls}>Notes</label>
                <textarea name="Notes" value={outscanForm.Notes} onChange={handleFormChange} rows={2}
                  placeholder="Additional notes…" className={`${inpCls} resize-y min-h-[56px]`}/></div>
            </div>
          </div>

          {/* Scanner section */}
          <div className={`rounded-lg p-5 mb-4 border-2 ${scanningMode?'bg-emerald-50 border-emerald-500':'bg-gray-50 border-gray-200'}`}>
            <div className={`flex items-center gap-2 text-sm font-semibold mb-0 px-3 py-2 rounded-md ${scanningMode?'bg-emerald-100 text-gray-700':'bg-gray-100 text-gray-700'}`}>
              <Scan size={16} style={{ color:scanningMode?TEAL:'#6b7280' }}/>
              Scan Consignments
              {scanningMode&&(
                <span className="ml-auto flex items-center gap-2 text-[13px] font-bold text-emerald-600">
                  <span className="inline-block w-2 h-2 bg-emerald-500 rounded-full" style={{ animation:'os-pulse 2s infinite' }}/>
                  READY TO SCAN
                </span>
              )}
            </div>
            <form onSubmit={handleScanSubmit}>
              <div className="flex gap-3 items-end mt-3.5 max-[768px]:flex-col">
                <div className="flex-1 flex flex-col gap-1.5">
                  <label className="text-[13px] font-bold text-gray-700">Consignment Number / Barcode</label>
                  <input ref={scanInputRef} type="text" value={scanInput}
                    onChange={e=>setScanInput(e.target.value)}
                    placeholder="Scan or enter consignment number…"
                    disabled={!scanningMode||loading} autoFocus
                    className={`w-full px-3.5 py-3 rounded-lg font-mono text-[17px] tracking-wide outline-none transition-all duration-150 box-border border-2 ${scanningMode?'bg-white border-emerald-500 focus:border-emerald-600 focus:shadow-[0_0_0_3px_rgba(16,185,129,0.15)]':'bg-gray-100 border-gray-300 cursor-not-allowed'}`}/>
                </div>
                <button type="submit" disabled={!scanningMode||loading||!scanInput.trim()}
                  className="flex items-center gap-2 px-5 py-3 border-0 rounded-lg text-sm font-semibold text-white cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-150 hover:-translate-y-px whitespace-nowrap h-[46px] max-[768px]:w-full max-[768px]:justify-center"
                  style={{ background:`linear-gradient(135deg,${VIOLET},${VIOLET_DK})` }}>
                  <Plus size={18}/> Scan
                </button>
              </div>
            </form>
          </div>

          {/* Stats */}
          {scannedItems.length>0&&(
            <div className="grid grid-cols-3 gap-3.5 mb-4 max-[768px]:grid-cols-1">
              {[
                { label:'Total Items Scanned', value:stats.totalItems,              bg:'bg-blue-50',  border:'border-blue-200',  lbl:'text-blue-700',  val:'text-blue-900'  },
                { label:'Total Weight (kg)',    value:stats.totalWeight.toFixed(2),  bg:'bg-emerald-50',border:'border-emerald-200',lbl:'text-emerald-700',val:'text-emerald-900'},
                { label:'Total Packages',      value:stats.totalPackages,           bg:'bg-amber-50', border:'border-amber-200', lbl:'text-amber-700', val:'text-amber-900' },
              ].map(c=>(
                <div key={c.label} className={`${c.bg} border ${c.border} rounded-lg px-4 py-4`}>
                  <div className={`text-[12px] font-semibold mb-1.5 ${c.lbl}`}>{c.label}</div>
                  <div className={`text-[30px] font-bold leading-none ${c.val}`}>{c.value}</div>
                </div>
              ))}
            </div>
          )}

          {/* Scanned Items */}
          <div className="bg-white rounded-lg p-5 border border-gray-200 shadow-sm">
            <div className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-3.5 pb-2.5 border-b-2 border-violet-100">
              <Package size={16} style={{ color:VIOLET }}/> Scanned Items ({scannedItems.length})
            </div>
            {scannedItems.length===0?(
              <div className="text-center py-12 text-gray-400 bg-gray-50 rounded-lg">
                <Scan size={48} className="mx-auto mb-3.5 opacity-25 block"/>
                <p className="text-[13.5px] m-0">No items scanned yet. Start scanning consignments above.</p>
              </div>
            ):(
              <div className="border border-gray-200 rounded-lg overflow-hidden overflow-x-auto">
                <table className="w-full border-collapse text-[13px] bg-white">
                  <thead className="bg-gradient-to-r from-violet-50 to-violet-100">
                    <tr className="border-b-2 border-violet-200">
                      {[['#','w-[44px]'],['Consignment No',''],['Date',''],['Shipper',''],['Consignee',''],['Destination',''],['Weight','text-right'],['Pkgs','text-right'],['Scanned At',''],['Del','text-center']].map(([h,cls])=>(
                        <th key={h} className={`px-3 py-3 text-left font-semibold text-violet-700 text-[11.5px] uppercase tracking-wide whitespace-nowrap ${cls}`}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {scannedItems.map((item,index)=>(
                      <tr key={index} className="os-tr border-b border-gray-100 last:border-b-0">
                        <td className="px-3 py-2.5 font-bold text-center" style={{ color:VIOLET }}>{index+1}</td>
                        <td className="px-3 py-2.5 font-mono font-bold text-[13px]" style={{ color:'#7a5cbf' }}>{item.ConsignmentNo}</td>
                        <td className="px-3 py-2.5 text-[12px]">{new Date(item.ConsignmentDate).toLocaleDateString()}</td>
                        <td className="px-3 py-2.5">{item.ShipperName||'–'}</td>
                        <td className="px-3 py-2.5">{item.ConsigneeName||'–'}</td>
                        <td className="px-3 py-2.5">{item.DestinationCity||'–'}</td>
                        <td className="px-3 py-2.5 text-right font-semibold">{parseFloat(item.Weight).toFixed(2)}</td>
                        <td className="px-3 py-2.5 text-right">{item.NoOfPkgs}</td>
                        <td className="px-3 py-2.5 text-[12px] text-gray-500">{new Date(item.ScannedAt).toLocaleTimeString()}</td>
                        <td className="px-3 py-2.5 text-center">
                          <button onClick={()=>handleRemoveItem(index)} title="Remove"
                            className="w-7 h-7 flex items-center justify-center rounded-md border-0 bg-none cursor-pointer text-red-500 hover:bg-red-50 hover:scale-110 transition-all duration-150 mx-auto">
                            <Trash2 size={14}/>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="bg-gray-100 border-t-2 border-violet-200">
                      <td colSpan={6} className="px-3 py-3 text-right font-semibold text-gray-600">Totals:</td>
                      <td className="px-3 py-3 text-right font-bold text-[15px]" style={{ color:'#7a5cbf' }}>{stats.totalWeight.toFixed(2)}</td>
                      <td className="px-3 py-3 text-right font-bold text-[15px]" style={{ color:'#7a5cbf' }}>{stats.totalPackages}</td>
                      <td colSpan={2}/>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Outscan;